import React, { useState, useLayoutEffect, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames/bind'

import useUIContext from 'context/ui'
import requestIdleCallback, { cancelIdleCallback } from 'lib/requestIdleCallback'

import ViewportEnter from 'components/motion/ViewportEnter'
import Portal from 'components/ui/Portal'
import Video from 'components/ui/Video'
import Image from 'components/ui/Image'
import PlayIcon from 'assets/svg/video-controls-play.inline.svg'
import Button from 'components/ui/Button'
import AspectRatio from 'components/ui/AspectRatio'
import ThemeWaypoint from 'components/ui/ThemeWaypoint'
import WistiaEmbed from 'components/ui/WistiaEmbed'
import VideoLightbox from 'components/ui/VideoLightbox'
import { BodyCopy } from 'components/ui/Typography'

import s from './Hero.module.scss'
const cn = classNames.bind(s)

const CTA = ({ show, showVideo }) => (
  <div className={cn('cta', { show })} onClick={showVideo}>
    <BodyCopy>
      <Button>Back to video</Button>
    </BodyCopy>
  </div>
)

CTA.propTypes = {
  show: PropTypes.bool,
  showVideo: PropTypes.func,
}

const Hero = ({
  embedId,
  subtitle,
  squareImage,
  squareVideoMP4,
  squareVideoWEBM,
  transitionStatus,
  scrollbar,
  ...props
}) => {
  const isHorizontal = useUIContext((s) => s.currentLayout) === 'horizontal'
  const toggleHiddenHeader = useUIContext((s) => s.toggleHiddenHeader)
  const setCurrentTheme = useUIContext((s) => s.setCurrentTheme)
  const setCursorIcon = useUIContext((s) => s.setCursorIcon)
  const [mobileVideoVisible, setIsMobileVideoVisible] = useState(false)
  const [showCTA, shouldShowCTA] = useState(false)
  const [isWistiaReady, setIsWistiaReady] = useState(false)

  const hasSquareMedia = squareImage && (!!squareVideoMP4?.url || !!squareVideoWEBM?.url)

  const showMobileVideo = () => {
    setIsMobileVideoVisible(true)
    toggleHiddenHeader(true)
    const id = requestIdleCallback(() => {
      toggleHiddenHeader(true)
      cancelIdleCallback(id)
    })
  }

  const hideMobileVideo = () => {
    setIsMobileVideoVisible(false)
    toggleHiddenHeader(false)
    setCursorIcon(null)
  }

  useLayoutEffect(() => {
    if (isHorizontal) return
    setCurrentTheme('dark')

    const getThemeOnScroll = () => {
      if (window.scrollY) return
      setCurrentTheme('dark')
    }

    window.addEventListener('scroll', getThemeOnScroll)
    return () => {
      window.removeEventListener('scroll', getThemeOnScroll)
    }
  }, [isHorizontal])

  const onEnter = () => {
    shouldShowCTA(false)
    toggleHiddenHeader(false)
  }

  const onExit = () => {
    shouldShowCTA(true)
    toggleHiddenHeader(true)
  }

  const onWistiaReady = () => {
    setCursorIcon(null)
    setIsWistiaReady(true)
  }

  const showContent = () => scrollbar?.scrollTo(window.innerWidth, 0, 700)
  const showVideo = () => scrollbar?.scrollTo(0, 0, 700)

  useEffect(() => {
    setCursorIcon('LOADING')
    return () => setCursorIcon(null)
  }, [])

  return (
    <div className={cn('wrapper')}>
      {/* after the video is out the viewport */}
      <ThemeWaypoint className={cn('waypoint')} after="light" />
      <ViewportEnter onEnter={onEnter} onExit={onExit} once={false} threshold={0.5}>
        <div className={cn('inner', { isWistiaReady })}>
          <AspectRatio className={cn('stretch', transitionStatus)} ratio={1} desktopRatio={16 / 9}>
            {!!isHorizontal && (
              <WistiaEmbed
                id={embedId}
                initialState="playing"
                options={{ silentAutoPlay: true, endVideoBehavior: 'loop' }}
                readyCallback={onWistiaReady}
              />
            )}
            {!isHorizontal && (
              <>
                {embedId !== '' && (
                  <button className={cn('cover')} onClick={showMobileVideo} aria-label="play">
                    <div className={cn('coverIcon', { isVisible: !mobileVideoVisible })}>
                      <PlayIcon />
                    </div>
                  </button>
                )}
                {hasSquareMedia && (
                  <div className={cn('media')}>
                    <pre>{JSON.stringify(props)}</pre>
                    {!!squareImage && (
                      <div className={cn('image')}>
                        <Image src={squareImage} alt={subtitle} />
                      </div>
                    )}
                    {(!!squareVideoMP4 || !!squareVideoWEBM) && (
                      <Video
                        className={cn('video')}
                        mp4={squareVideoMP4?.url || null}
                        webm={squareVideoWEBM?.url || null}
                        skipForMobile={false}
                      />
                    )}
                  </div>
                )}
                <VideoLightbox embedID={embedId} isVisible={mobileVideoVisible} onHide={hideMobileVideo} onTop />
              </>
            )}
          </AspectRatio>
        </div>
      </ViewportEnter>
      {!!isHorizontal && (
        <div className={cn('horizontalUI', transitionStatus)}>
          <div className={cn('subtitle')}>{subtitle && <BodyCopy as="h3">{subtitle}</BodyCopy>}</div>
          <div className={cn('scroll')} onClick={showContent}>
            <BodyCopy>
              <Button>Discover More</Button>
            </BodyCopy>
          </div>
        </div>
      )}
      {!!isHorizontal && (
        <Portal>
          <CTA show={showCTA} showVideo={showVideo} />
        </Portal>
      )}
    </div>
  )
}

Hero.propTypes = {
  embedId: PropTypes.string,
  subtitle: PropTypes.string,
  squareImage: PropTypes.string,
  squareVideoMP4: PropTypes.object,
  squareVideoWEBM: PropTypes.object,
  transitionStatus: PropTypes.string,
  scrollbar: PropTypes.object, // horizontal scrollbar instance
}

export default Hero
